import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Building2, CalendarClock, GitBranch, ArrowUpRight, Sparkles, AlertTriangle, ShieldAlert, ClipboardCheck, ArrowRight, CheckCircle2, Clock, FileDown, Loader2, BookOpen, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PageHero } from "@/components/PageHero";

function StatCard({ icon: Icon, label, value, to, testid }) {
  const inner = (
    <Card data-testid={testid} className="group relative overflow-hidden p-6 border-slate-200/70 hover:-translate-y-1 hover:shadow-lg hover:border-blue-300 transition-all duration-200 h-full">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-600 to-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="flex items-start justify-between">
        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-500 flex items-center justify-center shadow-sm">
          <Icon className="h-5 w-5 text-white" />
        </div>
        {to && <ArrowUpRight className="h-4 w-4 text-slate-300" />}
      </div>
      <div className="mt-4 font-display text-3xl font-semibold tracking-tight">{value ?? "—"}</div>
      <div className="label-caps mt-1">{label}</div>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { tenantId, currentOrg } = useTenant();
  const [stats, setStats] = useState({});
  const [deadlines, setDeadlines] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [gapsTotal, setGapsTotal] = useState(0);
  const [stale, setStale] = useState([]);
  const [dlCounts, setDlCounts] = useState({ overdue: 0, upcoming: 0 });
  const [audits, setAudits] = useState(0);
  const isConsultant = user?.role === "consultant";
  const guideHref = isConsultant ? "/manuali/manuale-consulente.html" : "/manuali/manuale-cliente.html";
  const [showWelcome, setShowWelcome] = useState(() => !localStorage.getItem("synesis_guide_welcome_v1"));
  const dismissWelcome = () => { localStorage.setItem("synesis_guide_welcome_v1", "1"); setShowWelcome(false); };

  useEffect(() => {
    const p = tenantId ? `?tenant_id=${tenantId}` : "";
    api.get(`/stats${p}`).then((r) => setStats(r.data)).catch(() => {});
    api.get(`/deadlines${p}`).then((r) => {
      const all = r.data;
      setDeadlines(all.filter((d) => d.in_avvicinamento || d.scaduta).slice(0, 6));
      setDlCounts({ overdue: all.filter((d) => d.scaduta).length, upcoming: all.filter((d) => d.in_avvicinamento).length });
    }).catch(() => {});
    if (isConsultant && tenantId) {
      api.get(`/insights/gaps?tenant_id=${tenantId}`).then((r) => { setGaps(r.data.gaps); setGapsTotal(r.data.total_gaps); }).catch(() => {});
      api.get(`/insights/stale?tenant_id=${tenantId}`).then((r) => setStale(r.data.stale)).catch(() => {});
      api.get(`/audit-plans?tenant_id=${tenantId}`).then((r) => setAudits((r.data || []).length)).catch(() => {});
    } else { setGaps([]); setGapsTotal(0); setStale([]); setAudits(0); }
  }, [tenantId, isConsultant]);

  const actions = [
    { show: gapsTotal > 0, icon: ShieldAlert, tone: "rose", count: gapsTotal, label: "Requisiti scoperti (gap)", to: "/copertura" },
    { show: stale.length > 0, icon: AlertTriangle, tone: "amber", count: stale.length, label: "Documenti da revisionare", to: "/documenti" },
    { show: dlCounts.overdue > 0, icon: CalendarClock, tone: "rose", count: dlCounts.overdue, label: "Scadenze scadute", to: "/scadenze" },
    { show: dlCounts.upcoming > 0, icon: Clock, tone: "amber", count: dlCounts.upcoming, label: "Scadenze in avvicinamento (30 gg)", to: "/scadenze" },
    { show: audits > 0, icon: ClipboardCheck, tone: "indigo", count: audits, label: "Audit pianificati", to: "/audit" },
  ].filter((a) => a.show);
  const toneMap = {
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
  };

  const [compliance, setCompliance] = useState([]);
  const [recentDocs, setRecentDocs] = useState([]);
  const [lastReview, setLastReview] = useState(null);
  const [pkgLoading, setPkgLoading] = useState(false);
  useEffect(() => {
    if (!isConsultant) {
      api.get(`/compliance`).then((r) => setCompliance(r.data.schemes || [])).catch(() => {});
      const p = tenantId ? `?tenant_id=${tenantId}` : "";
      api.get(`/documents${p}`).then((r) => setRecentDocs([...(r.data || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 6))).catch(() => {});
      api.get(`/management-reviews${p}`).then((r) => setLastReview((r.data || [])[0] || null)).catch(() => {});
    }
  }, [isConsultant, tenantId]);
  const overallCompliance = compliance.length ? Math.round(compliance.reduce((s, c) => s + (c.percent || 0), 0) / compliance.length) : null;
  const [presLoading, setPresLoading] = useState(false);
  const downloadPresentation = async () => {
    setPresLoading(true);
    try {
      const r = await api.get(`/presentazione.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "Synesis_AI_Presentazione.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* noop */ } finally { setPresLoading(false); }
  };
  const downloadPackage = async () => {
    setPkgLoading(true);
    try {
      const r = await api.get(`/exports/documents.zip`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "documenti.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* noop */ } finally { setPkgLoading(false); }
  };

  return (
    <div className="space-y-8">
      {showWelcome && (
        <div data-testid="welcome-guide-banner"
          className="relative overflow-hidden rounded-xl border border-indigo-400/25 bg-gradient-to-r from-indigo-500/20 via-blue-500/10 to-transparent px-4 py-4 sm:px-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <button onClick={dismissWelcome} data-testid="welcome-guide-dismiss-m"
            title="Chiudi" className="sm:hidden absolute top-2.5 right-2.5 text-slate-400 hover:text-white transition-colors">
            <X className="h-4 w-4" />
          </button>
          <span className="h-11 w-11 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-500 flex items-center justify-center shrink-0 shadow-sm">
            <BookOpen className="h-5 w-5 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-800">Benvenuto in Synesis AI 👋</div>
            <div className="text-sm text-slate-600">Non sai da dove iniziare? La Guida spiega passo passo tutte le funzioni della piattaforma.</div>
          </div>
          <a href={guideHref} target="_blank" rel="noopener noreferrer" data-testid="welcome-guide-open"
            className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white text-sm font-medium px-4 py-2 transition-colors">
            <BookOpen className="h-4 w-4" /> Apri la Guida
          </a>
          <button onClick={dismissWelcome} data-testid="welcome-guide-dismiss"
            title="Chiudi" className="hidden sm:block shrink-0 text-slate-400 hover:text-slate-700 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <PageHero
        label="Cruscotto"
        title={isConsultant ? (currentOrg ? currentOrg.nome : "Panoramica generale") : (currentOrg?.nome || "La tua organizzazione")}
        subtitle={isConsultant && !tenantId ? "Dati aggregati su tutti i clienti" : "Riepilogo del sistema di gestione documentale"}
      />

      {isConsultant && (
        <div className="flex justify-end -mt-2">
          <Button onClick={downloadPresentation} disabled={presLoading} data-testid="download-presentation-btn"
            variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
            {presLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
            Scarica presentazione piattaforma (PDF)
          </Button>
        </div>
      )}

      {(dlCounts.overdue > 0 || dlCounts.upcoming > 0) && (
        <Link to="/scadenze" data-testid="dashboard-deadline-banner"
          className={`flex items-center gap-3 rounded-xl border px-5 py-4 transition-colors ${dlCounts.overdue > 0 ? "bg-rose-50 border-rose-200 hover:bg-rose-100/70" : "bg-amber-50 border-amber-200 hover:bg-amber-100/70"}`}>
          <span className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 text-white ${dlCounts.overdue > 0 ? "bg-rose-600" : "bg-amber-500"}`}>
            {dlCounts.overdue > 0 ? <AlertTriangle className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
          </span>
          <div className="flex-1">
            <div className="font-semibold text-slate-800">
              {dlCounts.overdue > 0 ? `${dlCounts.overdue} scadenza/e già scaduta/e` : `${dlCounts.upcoming} scadenza/e in avvicinamento`}
              {dlCounts.overdue > 0 && dlCounts.upcoming > 0 ? ` · ${dlCounts.upcoming} in avvicinamento (30 gg)` : ""}
            </div>
            <div className="text-sm text-slate-600">Controlla l'agenda scadenze e agisci per tempo (soglia 30 giorni).</div>
          </div>
          <ArrowRight className="h-5 w-5 text-slate-400" />
        </Link>
      )}

      {!isConsultant && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="client-kpis">
          <Card className="relative overflow-hidden p-6 border-slate-200 bg-gradient-to-br from-blue-600 to-indigo-500 text-white">
            <div className="label-caps text-white/80">Conformità complessiva</div>
            <div className="font-display text-4xl font-semibold mt-2" data-testid="client-overall-compliance">{overallCompliance != null ? `${overallCompliance}%` : "—"}</div>
            <div className="text-xs text-white/80 mt-1">media sulle norme accreditate</div>
          </Card>
          <StatCard icon={Clock} label="Scadenze in avvicinamento" value={dlCounts.upcoming} to="/scadenze" testid="client-kpi-upcoming" />
          <StatCard icon={FileText} label="Documenti disponibili" value={stats.documenti} to="/documenti" testid="client-kpi-docs" />
        </div>
      )}

      {isConsultant && tenantId && (
        <Card className="border-slate-200 p-6" data-testid="action-center">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-500 flex items-center justify-center shadow-sm"><ClipboardCheck className="h-4 w-4 text-white" /></span>
            <h2 className="font-display text-lg font-semibold">Centro operativo</h2>
            <span className="text-xs text-slate-500">Cose da fare per {currentOrg?.nome}</span>
          </div>
          {actions.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3" data-testid="action-center-clear">
              <CheckCircle2 className="h-4 w-4" /> Tutto in regola: nessuna azione urgente per questo cliente.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="action-center-items">
              {actions.map((a, i) => (
                <div key={i} className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${toneMap[a.tone]}`}>
                  <a.icon className="h-5 w-5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-2xl font-bold leading-none">{a.count}</div>
                    <div className="text-xs mt-1 opacity-90">{a.label}</div>
                  </div>
                  <Link to={a.to} data-testid={`action-resolve-${i}`} className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold bg-white/70 hover:bg-white rounded-md px-2.5 py-1.5 transition-colors">
                    Risolvi ora <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <div className="rounded-2xl p-5 bg-gradient-to-br from-[#0A1734] via-[#0A1027] to-[#06090F] border border-white/10 shadow-xl relative overflow-hidden" data-testid="command-deck-kpis">
        <div className="pointer-events-none absolute -top-16 -right-10 h-48 w-48 rounded-full bg-indigo-600/20 blur-3xl" />
        <div className="relative flex items-center gap-2 mb-4">
          <span className="h-2 w-2 rounded-full bg-cyan-400 shadow-[0_0_8px_2px_rgba(34,211,238,.5)]" />
          <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-400">Panoramica</span>
        </div>
        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3">
          {(isConsultant
            ? [
                { label: "Clienti", value: stats.clienti, to: "/clienti", accent: "text-white" },
                { label: "Documenti", value: stats.documenti, to: "/documenti", accent: "text-white" },
                { label: "Processi", value: stats.processi, to: null, accent: "text-white" },
                { label: "Scadenze aperte", value: stats.scadenze_aperte, to: "/scadenze", accent: "text-amber-300" },
              ]
            : [
                { label: "Documenti", value: stats.documenti, to: "/documenti", accent: "text-white" },
                { label: "Processi", value: stats.processi, to: null, accent: "text-white" },
                { label: "Scadenze aperte", value: stats.scadenze_aperte, to: null, accent: "text-amber-300" },
              ]
          ).map((k, i) => {
            const inner = (
              <>
                <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{k.label}</div>
                <div className={`font-display text-3xl font-bold mt-1.5 ${k.accent}`}>{k.value ?? "—"}</div>
              </>
            );
            const cls = "rounded-xl bg-white/[0.05] border border-white/10 p-4 transition-colors";
            return k.to ? (
              <Link key={i} to={k.to} data-testid={`deck-kpi-${i}`} className={`${cls} hover:bg-white/[0.09]`}>{inner}</Link>
            ) : (
              <div key={i} data-testid={`deck-kpi-${i}`} className={cls}>{inner}</div>
            );
          })}
        </div>
      </div>

      {isConsultant && tenantId && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="consultant-insights">
          <Card className="border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4 text-blue-700" /><h2 className="font-display text-lg font-medium">Suggerimenti di Steve</h2></div>
            {gaps.length === 0 ? <p className="text-sm text-slate-500">Nessun requisito scoperto sugli schemi in uso.</p> : (
              <ul className="space-y-2" data-testid="insights-gaps">
                {gaps.map((g, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <Badge variant="outline" className="font-mono text-[10px] shrink-0">{g.scheme} {g.codice}</Badge>
                    <span className="text-slate-600">{g.titolo}</span>
                  </li>
                ))}
              </ul>
            )}
            {gapsTotal > gaps.length && <p className="text-xs text-slate-500 mt-3">+{gapsTotal - gaps.length} altri requisiti scoperti</p>}
          </Card>
          <Card className="border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-3"><AlertTriangle className="h-4 w-4 text-amber-600" /><h2 className="font-display text-lg font-medium">Documenti da revisionare</h2></div>
            {stale.length === 0 ? <p className="text-sm text-slate-500">Nessun documento obsoleto o senza revisione.</p> : (
              <ul className="space-y-2" data-testid="insights-stale">
                {stale.slice(0, 8).map((s) => (
                  <li key={s.id}>
                    <Link to={`/documenti/${s.id}`} className="text-sm flex items-center gap-2 hover:text-blue-700">
                      <span className="font-mono text-xs text-slate-500">{s.codice || "—"}</span>
                      <span className="truncate">{s.titolo}</span>
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] ml-auto shrink-0">{s.reason}</Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      {!isConsultant && (
        <Card className="border-slate-200 p-6" data-testid="client-compliance">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-medium">Conformità</h2>
            <Button variant="outline" size="sm" onClick={downloadPackage} disabled={pkgLoading} data-testid="download-package-btn">
              {pkgLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
              Scarica pacchetto documenti
            </Button>
          </div>
          {compliance.length === 0 ? (
            <p className="text-sm text-slate-500">Nessuno schema con requisiti disponibile.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="compliance-list">
              {compliance.map((c) => (
                <div key={c.sigla} className="rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm font-semibold">{c.sigla}</span>
                    <Badge className={c.percent >= 80 ? "bg-emerald-100 text-emerald-700 border-emerald-200" : c.percent >= 50 ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-rose-100 text-rose-700 border-rose-200"}>{c.percent}%</Badge>
                  </div>
                  <Progress value={c.percent} className="h-2 mt-3" />
                  <div className="text-xs text-slate-500 mt-2">{c.covered}/{c.total} requisiti coperti</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {!isConsultant && lastReview && (
        <Card className="border-slate-200 p-6" data-testid="client-review-box">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-500 flex items-center justify-center shadow-sm"><ClipboardCheck className="h-4 w-4 text-white" /></span>
            <h2 className="font-display text-lg font-medium">Ultimo riesame della direzione</h2>
            <span className="text-xs text-slate-500 ml-auto">{lastReview.data || "—"}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="font-display text-2xl font-semibold">{(lastReview.obiettivi || []).length}</div>
              <div className="label-caps mt-1">Obiettivi totali</div>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
              <div className="font-display text-2xl font-semibold text-emerald-700">{(lastReview.obiettivi || []).filter((o) => o.stato === "raggiunto").length}</div>
              <div className="label-caps mt-1">Raggiunti</div>
            </div>
            <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-4">
              <div className="font-display text-2xl font-semibold text-blue-700">{(lastReview.obiettivi || []).filter((o) => o.stato === "aperto").length}</div>
              <div className="label-caps mt-1">Aperti</div>
            </div>
          </div>
          {(lastReview.outputs || []).length > 0 && (
            <div className="mt-4">
              <div className="label-caps mb-1">Principali output</div>
              <ul className="list-disc pl-5 text-sm text-slate-600 space-y-0.5">
                {lastReview.outputs.slice(0, 4).map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            </div>
          )}
        </Card>
      )}

      {!isConsultant && (
        <Card className="border-slate-200 p-6" data-testid="client-recent-docs">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-medium">Ultimi documenti</h2>
            <Link to="/documenti" className="text-sm text-blue-700 hover:underline">Vedi tutti →</Link>
          </div>
          {recentDocs.length === 0 ? (
            <p className="text-sm text-slate-500">Nessun documento disponibile.</p>
          ) : (
            <div className="space-y-2">
              {recentDocs.map((d) => (
                <Link key={d.id} to={`/documenti/${d.id}`} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 hover:text-blue-700">
                  <span className="font-mono text-xs text-slate-500 shrink-0">{d.codice || "—"}</span>
                  <span className="truncate text-sm flex-1">{d.titolo}</span>
                  <Badge variant="outline" className="text-[10px] shrink-0 capitalize">{d.tipo}</Badge>
                </Link>
              ))}
            </div>
          )}
        </Card>
      )}

      <Card className="border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-medium">Scadenze in evidenza</h2>
          {isConsultant && <Link to="/scadenze" className="text-sm text-blue-700 hover:underline">Vedi agenda →</Link>}
        </div>
        {deadlines.length === 0 ? (
          <p className="text-sm text-slate-500">Nessuna scadenza imminente o scaduta.</p>
        ) : (
          <div className="space-y-2" data-testid="dashboard-deadlines">
            {deadlines.map((d) => (
              <div key={d.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <div>
                  <div className="text-sm font-medium capitalize">{d.tipo}</div>
                  <div className="text-xs text-slate-500">{d.descrizione}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">{d.data_scadenza}</span>
                  {d.scaduta ? (
                    <Badge className="bg-red-100 text-red-700 border-red-200">Scaduta</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">{d.giorni_rimanenti} gg</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
